var _effectorFileName = "/builds/gmini/front-sm-api-sdk/src/UserClassifierRepo/UserClassifier.ts";
import "core-js/modules/es.function.name.js";
import * as t from 'io-ts';
import { ApiCallService } from '@gmini/api-call-service';
import * as Sentry from '@sentry/browser';
import { merge } from 'effector';
import { Id } from '../Id';
import { Version, VersionData, VersionDates, Versions } from '../Version';
import { BaseClassifier } from '../BaseClassifierRepo';
import { createPredicate } from '../createPredicate';
import { BimElement } from '../BimEntity';
import { ApiDomain } from '../domain';
import { FlatListDependencies } from '../FlatListDependencies';
import { ElementRefItems } from '../ElementRefItems';
import { DynamicGroupFlatItems } from '../DynamicFlatListItems';
import { AnonymousClassifier } from '../AnonymousClassifier';
import { Urn } from '../Urn';
import { errorNameMap } from '../logApiError';
import { AssemblyClassifier } from './AssemblyClassifier';
import { UserClassifierGroup } from './UserClassifierGroup';
export var UserClassifier;

(function (_UserClassifier) {
  var domain = ApiDomain.domain('UserClassifier', {
    name: "domain",
    sid: "-en58dk"
  });
  var type = _UserClassifier.type = 'UserClassifier';
  var io = _UserClassifier.io = t.recursion('UserClassifier', function () {
    return t.type({
      type: t.literal(type),
      id: Id,
      version: Version,
      parentFolderId: t.union([t["null"], Id]),
      sourceClassifiers: t.readonlyArray(t.union([BaseClassifier.io, UserClassifier.io, AssemblyClassifier.io, AnonymousClassifier.io])),
      name: t.string,
      total: t.number,
      readOnly: t["boolean"],
      owner: t.union([t["null"], t.string]),
      versionNameOwner: t.union([t["null"], t.string]),
      versionCreatedDate: t.string,
      versionName: t.union([t["null"], t.string]),
      projectUrn: Urn
    });
  });
  var is = _UserClassifier.is = createPredicate(type);
  var Populated;

  (function (_Populated) {
    var type = _Populated.type = 'UserClassifierPopulated';
    var io = _Populated.io = t.recursion('UserClassifierPopulated', function () {
      return t.type({
        type: t.literal(type),
        id: Id,
        version: Version,
        parentFolderId: t.union([t["null"], Id]),
        sourceClassifiers: t.readonlyArray(t.union([BaseClassifier.io, UserClassifier.io, AssemblyClassifier.io, AnonymousClassifier.io])),
        name: t.string,
        total: t.number,
        children: t.readonlyArray(UserClassifierGroup.io),
        offset: t.number,
        readOnly: t["boolean"],
        owner: t.union([t["null"], t.string]),
        versionNameOwner: t.union([t["null"], t.string]),
        versionCreatedDate: t.string,
        versionName: t.union([t["null"], t.string]),
        projectUrn: Urn
      });
    });
    var is = _Populated.is = createPredicate(type);
  })(Populated || (Populated = _UserClassifier.Populated || (_UserClassifier.Populated = {})));

  var create = _UserClassifier.create = ApiCallService.create({
    name: 'create',
    parentDomain: domain,
    result: io,
    getKey: ['name', 'parentFolderId', 'projectUrn']
  });
  var fetchIsUserClassifierLocked = _UserClassifier.fetchIsUserClassifierLocked = ApiCallService.create({
    name: 'isClassifierLock',
    parentDomain: domain,
    result: t["boolean"],
    getKey: ['id']
  });
  var copy = _UserClassifier.copy = ApiCallService.create({
    name: 'copy',
    parentDomain: domain,
    result: UserClassifier.io,
    getKey: ['name', 'id', 'version']
  });
  var fetch = _UserClassifier.fetch = ApiCallService.create({
    name: 'fetch',
    parentDomain: domain,
    result: Populated.io,
    getKey: ['id', 'version']
  });
  var getMostRecent = _UserClassifier.getMostRecent = ApiCallService.create({
    name: 'getMostRecent',
    parentDomain: domain,
    result: Populated.io,
    getKey: ['id']
  });
  var getMostRecentSilent = _UserClassifier.getMostRecentSilent = ApiCallService.create({
    name: 'getMostRecentSilent',
    parentDomain: domain,
    result: Populated.io,
    getKey: ['id']
  });
  var getElementFromViewer = _UserClassifier.getElementFromViewer = ApiCallService.create({
    name: 'getElementFromViewer',
    parentDomain: domain,
    result: BimElement.io,
    getKey: ['modelId', 'modelVersion', 'externalId']
  });
  var rename = _UserClassifier.rename = ApiCallService.create({
    name: 'rename',
    parentDomain: domain,
    result: io,
    getKey: ['id', 'version']
  });
  var move = _UserClassifier.move = ApiCallService.create({
    name: 'move',
    parentDomain: domain,
    result: io,
    getKey: ['id', 'version']
  });
  var remove = _UserClassifier.remove = ApiCallService.create({
    name: 'remove',
    parentDomain: domain,
    result: io,
    getKey: ['id']
  });
  var removeAll = _UserClassifier.removeAll = ApiCallService.create({
    name: 'removeAll',
    parentDomain: domain,
    result: io,
    getKey: ['id', 'version']
  });
  var addDependency = _UserClassifier.addDependency = ApiCallService.create({
    name: 'addDependency',
    parentDomain: domain,
    result: io,
    getKey: ['id', 'version', 'sourceClassifierId', 'sourceClassifierVersion']
  });
  var removeDependency = _UserClassifier.removeDependency = ApiCallService.create({
    name: 'removeDependency',
    parentDomain: domain,
    result: io,
    getKey: ['id', 'version', 'sourceClassifierId']
  });
  var fetchVersionDates = _UserClassifier.fetchVersionDates = ApiCallService.create({
    name: 'modelElementEstimationStatus',
    parentDomain: domain,
    result: VersionDates.io,
    getKey: ['classifierId']
  });
  var fetchVersionByDate = _UserClassifier.fetchVersionByDate = ApiCallService.create({
    name: 'modelElementEstimationStatus',
    parentDomain: domain,
    result: Versions.io,
    getKey: ['classifierId']
  });
  var renameVersion = _UserClassifier.renameVersion = ApiCallService.create({
    name: 'renameVersion',
    parentDomain: domain,
    result: VersionData.io,
    getKey: ['classifierId', 'classifierVersion', 'versionName']
  });
  var removeVersionName = _UserClassifier.removeVersionName = ApiCallService.create({
    name: 'removeVersionName',
    parentDomain: domain,
    result: VersionData.io,
    getKey: ['classifierId', 'classifierVersion']
  });
  var fetchNamedVersions = _UserClassifier.fetchNamedVersions = ApiCallService.create({
    name: 'fetchNamedVersions',
    parentDomain: domain,
    result: Versions.io,
    getKey: ['classifierId']
  });
  var createRefsFromExternalIds = _UserClassifier.createRefsFromExternalIds = ApiCallService.create({
    name: 'createRefsFromExternalIds',
    parentDomain: domain,
    result: UserClassifier.io,
    getKey: ['id', 'version', 'parentGroupId']
  });
  var fetchFlatListDependencies = _UserClassifier.fetchFlatListDependencies = ApiCallService.create({
    name: 'fetchFlatListDependencies',
    parentDomain: domain,
    result: FlatListDependencies.io,
    getKey: ['classifierId', 'classifierVersion']
  });
  var fetchFlatListItems = _UserClassifier.fetchFlatListItems = ApiCallService.create({
    name: 'fetchFlatListItems',
    parentDomain: domain,
    result: FlatListDependencies.io,
    getKey: ['classifierId', 'classifierVersion', 'skipRef']
  });
  var fetchDynamicGroupFlatItems = _UserClassifier.fetchDynamicGroupFlatItems = ApiCallService.create({
    name: 'fetchDynamicGroupFlatItems',
    parentDomain: domain,
    result: DynamicGroupFlatItems.io,
    getKey: ['classifierId', 'classifierVersion']
  });
  var fetchElementRefs = _UserClassifier.fetchElementRefs = ApiCallService.create({
    name: 'fetchElementRefs',
    parentDomain: domain,
    result: ElementRefItems.io,
    getKey: ['elementId']
  });
  var findDynamicGroups = _UserClassifier.findDynamicGroups = ApiCallService.create({
    name: 'findDynamicGroups',
    parentDomain: domain,
    result: t.type({
      items: t.array(Id)
    }),
    getKey: ['classifierId', 'classifierVersion', 'elementId', 'modelId', 'modelVersion']
  });
  var FindAllElementByFilterRes = _UserClassifier.FindAllElementByFilterRes = t.type({
    elements: t.array(t.intersection([BimElement.io, t.type({
      refIds: t.array(Id),
      parentDynamicGroupIds: t.array(Id)
    })]))
  });
  var findAllElementsByFilter = _UserClassifier.findAllElementsByFilter = ApiCallService.create({
    name: 'findAllElementByFilter',
    parentDomain: domain,
    result: FindAllElementByFilterRes,
    getKey: ['classifierId', 'classifierVersion']
  });
  var FindEverywhereBimElement = _UserClassifier.FindEverywhereBimElement = t.intersection([BimElement.io, t.type({
    parentGroupId: t.union([Id, t["null"]]),
    parentGroupType: t.union([t.literal('UserClassifierGroupWithElements'), t.literal('DynamicGeneratedGroup'), t["null"]])
  })]);
  var FindEverywhereBimElementRes = _UserClassifier.FindEverywhereBimElementRes = t.type({
    elements: t.array(FindEverywhereBimElement)
  });
  var findElementEverywhere = _UserClassifier.findElementEverywhere = ApiCallService.create({
    name: 'findElementEverywhere',
    parentDomain: domain,
    result: FindEverywhereBimElementRes,
    getKey: ['classifierId', 'classifierVersion', 'elementId', 'modelId', 'version']
  });
  var Property;

  (function (_Property) {
    var domain = ApiDomain.domain('Property', {
      name: "domain",
      sid: "iapijf"
    });
    var io = _Property.io = t.type({
      id: Id,
      name: t.string,
      groupName: t.string
    }, 'Property');
    var List;

    (function (_List) {
      var io = _List.io = t.array(Property.io);
      var ElementProperties;

      (function (_ElementProperties) {
        var io = _ElementProperties.io = t.type({
          elementProperties: Property.List.io
        });
      })(ElementProperties || (ElementProperties = _List.ElementProperties || (_List.ElementProperties = {})));

      var fetchElementProperties = _List.fetchElementProperties = ApiCallService.create({
        name: 'fetchElementProperties',
        parentDomain: domain,
        result: ElementProperties.io,
        getKey: ['classifierId', 'classifierVersion']
      });
      var FetchElementPropertyValuesRes = _List.FetchElementPropertyValuesRes = t.intersection([Property.io, t.type({
        values: t.array(t.string)
      })]);
      var fetchElementPropertyValues = _List.fetchElementPropertyValues = ApiCallService.create({
        name: 'fetchElementPropertyValues',
        parentDomain: domain,
        result: t.union([FetchElementPropertyValuesRes, t["null"]]),
        getKey: ['classifierId', 'classifierVersion', 'propertyId']
      });
      merge([fetchElementProperties.fail], {
        sid: "-8aqdf6"
      }).watch(function (fail) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (fail.error.name !== 'AbortError') {
          Sentry.captureEvent({
            message: errorNameMap[fail.error.status],
            extra: fail
          });
        }
      });
    })(List || (List = _Property.List || (_Property.List = {})));
  })(Property || (Property = _UserClassifier.Property || (_UserClassifier.Property = {})));
})(UserClassifier || (UserClassifier = {}));